import React, { useState, useRef, useEffect } from 'react';
import { Input, Label } from 'reactstrap';
import Required from 'components/Required';
import { AutorizacionIOMA } from 'types/AutorizacionesIOMA';
import { Diagnostico } from 'types/Diagnosticos';

interface DiagnosticoSelectProps {
  diagnosticos: Diagnostico[];
  data: AutorizacionIOMA;
  handleInputChange: (e: any) => void;
  isLoading: boolean;
  formValidation: { diagnostico?: boolean };
}

function DiagnosticoSelect({
  diagnosticos,
  data,
  handleInputChange,
  isLoading,
  formValidation,
}: DiagnosticoSelectProps) {
  const [search, setSearch] = useState(data.diagnostico || '');
  const [showOptions, setShowOptions] = useState(false);

  const [selected, setSelected] = useState<Partial<Diagnostico> | null>(
    diagnosticos.find(d => d.descripcion === data.diagnostico) || null,
  );

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSearch(data.diagnostico || '');
  }, [data.diagnostico]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowOptions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = diagnosticos.filter(item =>
    item.descripcion.toLowerCase().includes(search.toLowerCase()),
  );

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value.length > 60) {
      return;
    }

    setSearch(value);
    setShowOptions(true);

    setSelected(null);

    handleInputChange({
      target: { name: 'diagnostico', value: value },
    });
  };

  const handleSelect = (item: Diagnostico) => {
    setSelected(item);
    setSearch(item.descripcion);
    setShowOptions(false);

    handleInputChange({
      target: { name: 'diagnostico', value: item.descripcion },
    });
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <Label for="diagnostico">
        Diagnóstico <Required />
      </Label>
      <Input
        id="diagnostico"
        name="diagnostico"
        type="text"
        placeholder="Escriba o seleccione un diagnóstico..."
        value={search}
        onChange={handleTextChange}
        onFocus={() => setShowOptions(true)}
        invalid={formValidation.diagnostico === false}
        disabled={isLoading}
        required
        autoComplete="off"
        maxLength={60}
      />

      {showOptions && filteredOptions.length > 0 && search.length > 0 && (
        <div
          className="dropdown-menu show"
          style={{
            display: 'block',
            width: '100%',
            maxHeight: '180px',
            overflowY: 'auto',
            position: 'absolute',
            top: '100%',
            left: 0,
            zIndex: 1000,
          }}
        >
          {filteredOptions.map(item => (
            <button
              key={item.id}
              type="button"
              className={`dropdown-item ${
                selected && selected.id === item.id ? 'active' : ''
              }`}
              onClick={() => handleSelect(item)}
            >
              {item.descripcion}
            </button>
          ))}
        </div>
      )}

      {showOptions && filteredOptions.length === 0 && search.length > 0 && (
        <div
          className="dropdown-menu show"
          style={{
            display: 'block',
            width: '100%',
            position: 'absolute',
            top: '100%',
            left: 0,
            zIndex: 1000,
          }}
        >
          <div className="dropdown-item text-muted" style={{ cursor: 'default' }}>
            Usando como texto libre: &quot;{search}&quot;
          </div>
        </div>
      )}
    </div>
  );
}

export default DiagnosticoSelect;
